'use strict';

const libPictView = require('pict-view');
const libDocHistory = require('pict-section-dochistory');
const libFrameworkClient = require('../providers/FrameworkClient.js');

/**
 * FoundationDocs - the "Documentation" surface. Hosts
 * pict-section-inlinedocumentation (a path-based markdown browser) inside the
 * workspace, backed by the Docs Lake API at /1.0/docs/*. The lake is the single
 * source of truth for documentation, so this surface never spins up a second
 * content system in the DB.
 *
 * Authoring here is first class and equal to authoring in a repository: with the
 * content.edit privilege the host wires save, create folder / document, delete,
 * move and reorder straight through to the lake's structure API, and every one of
 * them is an ordinary lake commit. A plan sheet that syncs no repository at all is
 * a complete home for a documentation library; a GitHub binding is optional and
 * changes nothing about how this surface writes.
 */
class PictViewFoundationDocs extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._loadSlug = 'index.md';
		this._vocabTooltipHandles = [];
		this._vocabClickHandlerInstalled = false;
	}

	_provider() { return this.pict.providers['Pict-InlineDocumentation']; }

	// ─── vocabulary auto-markup (WI #184) ────────────────────────────────
	// The resolver map ({ word -> {slug, title, short} }) is fetched once and cached on AppData (this
	// view's own instance is not guaranteed to survive navigation, AppData is), so every Foundation
	// Docs page after the first paints highlights instantly.
	_vocabularyCache()
	{
		if (!this.pict.AppData.VocabularyCache) { this.pict.AppData.VocabularyCache = { Map: null, Promise: null }; }
		return this.pict.AppData.VocabularyCache;
	}

	_loadVocabularyMap()
	{
		let tmpCache = this._vocabularyCache();
		if (tmpCache.Map) { return Promise.resolve(tmpCache.Map); }
		if (tmpCache.Promise) { return tmpCache.Promise; }
		tmpCache.Promise = libFrameworkClient.getJSON(this.pict, '/1.0/docs/_vocabulary.json')
			.then((pResult) => { tmpCache.Map = (pResult && pResult.Terms) || {}; return tmpCache.Map; })
			.catch(() => { tmpCache.Map = {}; return tmpCache.Map; });
		return tmpCache.Promise;
	}

	// (pWord) => {slug, title, short} | null -- the contract pict-section-content's parseInline()
	// calls. Reads the live cache object (not a snapshot), so it starts resolving the instant the
	// fetch above lands even if that happens after this function was handed to the provider.
	_vocabularyResolver(pWord)
	{
		let tmpMap = this._vocabularyCache().Map;
		if (!tmpMap) { return null; }
		return tmpMap[String(pWord || '').toLowerCase()] || null;
	}

	_escapeHTML(pText)
	{
		return String(pText || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	// Scan the rendered content for .pict-vocab-term spans (wrapped by pict-section-content's
	// _applyVocabularyLinks) and attach a hover popover to each, reusing pict-section-modal's
	// richTooltip -- the same tooltip primitive the inline-doc module's own Topic tooltips use.
	// Called after every displayContent(), so old handles (pointing at now-detached elements from
	// the previous document) are torn down first.
	_wireVocabPopovers()
	{
		for (let i = 0; i < this._vocabTooltipHandles.length; i++)
		{
			if (this._vocabTooltipHandles[i] && typeof this._vocabTooltipHandles[i].destroy === 'function')
			{
				this._vocabTooltipHandles[i].destroy();
			}
		}
		this._vocabTooltipHandles = [];

		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.richTooltip !== 'function' || typeof document === 'undefined') { return; }

		let tmpContainer = document.getElementById('InlineDoc-Container');
		if (!tmpContainer) { return; }

		let tmpElements = tmpContainer.querySelectorAll('.pict-vocab-term');
		for (let i = 0; i < tmpElements.length; i++)
		{
			let tmpElement = tmpElements[i];
			let tmpTitle = tmpElement.getAttribute('data-vocab-title') || '';
			let tmpShort = tmpElement.getAttribute('data-vocab-short') || '';
			let tmpSlug = tmpElement.getAttribute('data-vocab-slug') || '';
			let tmpHTML = '<div class="pict-vocab-popover">'
				+ '<div class="pict-vocab-popover-title">' + this._escapeHTML(tmpTitle) + '</div>'
				+ (tmpShort ? '<div class="pict-vocab-popover-short">' + this._escapeHTML(tmpShort) + '</div>' : '')
				+ '<a href="javascript:void(0)" class="pict-vocab-popover-link" data-vocab-open="' + this._escapeHTML(tmpSlug) + '">View definition &rarr;</a>'
				+ '</div>';
			this._vocabTooltipHandles.push(tmpModal.richTooltip(tmpElement, tmpHTML, { interactive: true, maxWidth: '320px' }));
		}

		this._installVocabClickHandler();
	}

	// One document-level delegated listener (installed once) for the popover's "View definition"
	// link -- the tooltip DOM node is created lazily by pict-modal-tooltip on hover, so there is
	// nothing to bind a per-element listener to until the popover is actually showing.
	_installVocabClickHandler()
	{
		if (this._vocabClickHandlerInstalled || typeof document === 'undefined') { return; }
		this._vocabClickHandlerInstalled = true;
		let tmpView = this;
		document.addEventListener('click', (pEvent) =>
		{
			let tmpTarget = pEvent.target;
			while (tmpTarget && tmpTarget !== document)
			{
				if (tmpTarget.hasAttribute && tmpTarget.hasAttribute('data-vocab-open'))
				{
					let tmpSlug = tmpTarget.getAttribute('data-vocab-open');
					let tmpProvider = tmpView._provider();
					if (tmpSlug && tmpProvider && typeof tmpProvider.loadDocument === 'function')
					{
						// Vocabulary docs always live under vocabulary/ (the folder VocabularyDictionary
						// reads); the .pict-vocab-term span only carries the bare slug, not a full path.
						tmpProvider.loadDocument('vocabulary/' + tmpSlug + '.md');
					}
					return;
				}
				tmpTarget = tmpTarget.parentNode;
			}
		});
	}

	// Every render call site the module offers (a fresh load, an edit save, the image viewer) funnels
	// through the content view's displayContent(), so patching it once here catches vocab spans from
	// all of them -- no need to also patch loadDocument/saveEdit separately.
	_wireVocabRenderHook()
	{
		let tmpContentView = this.pict.views['InlineDoc-Content'];
		if (!tmpContentView || tmpContentView._vocabRenderHookPatched || typeof tmpContentView.displayContent !== 'function') { return; }
		let tmpView = this;
		let tmpOriginal = tmpContentView.displayContent.bind(tmpContentView);
		tmpContentView.displayContent = function (pHTMLContent)
		{
			let tmpResult = tmpOriginal(pHTMLContent);
			tmpView._wireVocabPopovers();
			tmpView._enhanceDocSvgs();
			return tmpResult;
		};
		tmpContentView._vocabRenderHookPatched = true;
	}

	// ─── smarter SVG diagram backing ─────────────────────────────────────
	// Diagram SVGs ship as dark ink on a transparent canvas and vanish on a dark page, so Brand-Chrome
	// backs every .svg <img> with a light "paper" card by default. That is wrong for an SVG that already
	// paints its own background, or that is drawn in light ink (already dark-mode-friendly). An <img> is
	// opaque to the DOM, so we fetch the SVG source (same-origin, from the lake), inspect it, and tag the
	// ones that do NOT need paper with .pict-content-svg-bare -- which strips the card back off.
	_enhanceDocSvgs()
	{
		if (typeof document === 'undefined' || typeof fetch === 'undefined') { return; }
		let tmpRoot = document.querySelector('.ps-fdocs .pict-content');
		if (!tmpRoot) { return; }
		let tmpImgs = tmpRoot.querySelectorAll('img[src*=".svg"]');
		for (let i = 0; i < tmpImgs.length; i++)
		{
			let tmpImg = tmpImgs[i];
			if (tmpImg.getAttribute('data-svg-paper-checked')) { continue; }
			tmpImg.setAttribute('data-svg-paper-checked', '1');
			this._classifyDocSvg(tmpImg);
		}
	}

	_classifyDocSvg(pImg)
	{
		let tmpSrc = pImg.getAttribute('src');
		if (!tmpSrc) { return; }
		let tmpSelf = this;
		fetch(tmpSrc).then((pResp) => (pResp && pResp.ok) ? pResp.text() : '').then((pText) =>
		{
			// Default is paper; only strip it when the SVG clearly does not need it.
			if (pText && !tmpSelf._svgNeedsPaper(pText)) { pImg.classList.add('pict-content-svg-bare'); }
		}).catch(() => { /* on any failure, leave the default paper in place */ });
	}

	// An SVG needs the paper card when it paints NO background of its own AND its ink is dark (so it would
	// disappear on a dark page). An SVG that paints its own background, or is drawn in light ink, is left
	// bare. An unparseable SVG defaults to paper (the safe choice for this corpus).
	_svgNeedsPaper(pText)
	{
		try
		{
			let tmpDoc = new DOMParser().parseFromString(pText, 'image/svg+xml');
			if (tmpDoc.querySelector('parsererror')) { return true; }
			let tmpSVG = tmpDoc.querySelector('svg');
			if (!tmpSVG) { return true; }
			// 1) Own background? A non-transparent background on the root style, or a near-origin full-canvas rect.
			if (/background(-color)?\s*:\s*(?!\s*(transparent|none))/i.test(tmpSVG.getAttribute('style') || '')) { return false; }
			// Canvas size from the viewBox (fall back to width/height) so a full-canvas background rect is
			// recognized at ANY diagram size, not just large ones.
			let tmpVB = (tmpSVG.getAttribute('viewBox') || '').split(/[\s,]+/).map((p) => parseFloat(p));
			let tmpCanvasW = (tmpVB.length === 4 && !isNaN(tmpVB[2])) ? tmpVB[2] : (parseFloat(tmpSVG.getAttribute('width')) || 0);
			let tmpCanvasH = (tmpVB.length === 4 && !isNaN(tmpVB[3])) ? tmpVB[3] : (parseFloat(tmpSVG.getAttribute('height')) || 0);
			let tmpRects = tmpSVG.getElementsByTagName('rect');
			for (let i = 0; i < tmpRects.length; i++)
			{
				let tmpR = tmpRects[i];
				let tmpFill = (tmpR.getAttribute('fill') || '').trim().toLowerCase();
				if (!tmpFill || tmpFill === 'none' || tmpFill === 'transparent') { continue; }
				let tmpX = parseFloat(tmpR.getAttribute('x') || '0');
				let tmpY = parseFloat(tmpR.getAttribute('y') || '0');
				let tmpW = tmpR.getAttribute('width') || '';
				let tmpH = tmpR.getAttribute('height') || '';
				let tmpFullW = /%/.test(tmpW) || (tmpCanvasW ? parseFloat(tmpW) >= tmpCanvasW * 0.92 : parseFloat(tmpW) >= 100);
				let tmpFullH = /%/.test(tmpH) || (tmpCanvasH ? parseFloat(tmpH) >= tmpCanvasH * 0.92 : parseFloat(tmpH) >= 100);
				let tmpNearOriginX = Math.abs(tmpX) <= (tmpCanvasW ? tmpCanvasW * 0.04 + 2 : 4);
				let tmpNearOriginY = Math.abs(tmpY) <= (tmpCanvasH ? tmpCanvasH * 0.04 + 2 : 4);
				if (tmpFullW && tmpFullH && tmpNearOriginX && tmpNearOriginY) { return false; }
			}
			// 2) Transparent canvas. Dark ink -> needs paper; light ink -> already fine on a dark page.
			return this._svgInkLuminance(tmpSVG) < 0.5;
		}
		catch (pErr) { return true; }
	}

	// Average luminance (0 dark .. 1 light) of the SVG's ink. A fillable element with no fill paints black
	// by default, so a plain black-text diagram reads as dark. Sampled and capped so a huge SVG stays cheap.
	_svgInkLuminance(pSVG)
	{
		let tmpEls = pSVG.querySelectorAll('path, text, tspan, line, polyline, polygon, circle, ellipse');
		let tmpLimit = Math.min(tmpEls.length, 400);
		let tmpTotal = 0;
		let tmpCount = 0;
		for (let i = 0; i < tmpLimit; i++)
		{
			let tmpEl = tmpEls[i];
			let tmpTag = tmpEl.tagName.toLowerCase();
			let tmpFill = (tmpEl.getAttribute('fill') || '').trim().toLowerCase();
			let tmpStroke = (tmpEl.getAttribute('stroke') || '').trim().toLowerCase();
			let tmpFillable = /path|text|tspan|polygon|circle|ellipse/.test(tmpTag);
			if (tmpFillable && tmpFill === '' && tmpStroke === '') { tmpTotal += 0; tmpCount++; continue; } // default black ink
			let tmpColors = [tmpFill, tmpStroke].filter((c) => c && c !== 'none' && c !== 'transparent' && c !== 'currentcolor');
			for (let j = 0; j < tmpColors.length; j++)
			{
				let tmpL = this._colorLuminance(tmpColors[j]);
				if (tmpL !== null) { tmpTotal += tmpL; tmpCount++; }
			}
		}
		return tmpCount ? (tmpTotal / tmpCount) : 0;
	}

	// Parse a CSS color to relative luminance 0..1. Handles #rgb / #rrggbb, rgb()/rgba(), and a few names.
	// Returns null for colors it cannot read (keywords it does not know, url() paints, etc.).
	_colorLuminance(pColor)
	{
		let tmpC = String(pColor).trim().toLowerCase();
		let tmpNamed = { black: [0, 0, 0], white: [255, 255, 255], gray: [128, 128, 128], grey: [128, 128, 128], silver: [192, 192, 192], red: [255, 0, 0], green: [0, 128, 0], blue: [0, 0, 255] };
		let tmpR;
		let tmpG;
		let tmpB;
		if (Object.prototype.hasOwnProperty.call(tmpNamed, tmpC)) { tmpR = tmpNamed[tmpC][0]; tmpG = tmpNamed[tmpC][1]; tmpB = tmpNamed[tmpC][2]; }
		else if (tmpC.charAt(0) === '#')
		{
			let tmpHex = tmpC.slice(1);
			if (tmpHex.length === 3) { tmpHex = tmpHex.charAt(0) + tmpHex.charAt(0) + tmpHex.charAt(1) + tmpHex.charAt(1) + tmpHex.charAt(2) + tmpHex.charAt(2); }
			if (tmpHex.length < 6) { return null; }
			tmpR = parseInt(tmpHex.slice(0, 2), 16); tmpG = parseInt(tmpHex.slice(2, 4), 16); tmpB = parseInt(tmpHex.slice(4, 6), 16);
		}
		else
		{
			let tmpMatch = tmpC.match(/rgba?\(([^)]+)\)/);
			if (!tmpMatch) { return null; }
			let tmpParts = tmpMatch[1].split(',').map((p) => parseFloat(p));
			tmpR = tmpParts[0]; tmpG = tmpParts[1]; tmpB = tmpParts[2];
		}
		if (isNaN(tmpR) || isNaN(tmpG) || isNaN(tmpB)) { return null; }
		return (0.2126 * tmpR + 0.7152 * tmpG + 0.0722 * tmpB) / 255;
	}

	// ─── version history (WI #187) ───────────────────────────────────────
	// pict-section-dochistory ships two independent views and no transport; the host
	// registers them and supplies the two reads. They are registered lazily, the first time
	// someone opens history, so a reader that never asks for it pays nothing.
	_ensureHistoryViews()
	{
		if (this._historyViewsRegistered) { return; }
		if (!this.pict.views['Plansheet-DocHistory-Timeline'])
		{
			this.pict.addView('Plansheet-DocHistory-Timeline',
				Object.assign({}, libDocHistory.TimelineView.default_configuration,
					{
						Title: '',
						onLoadHistory: (pPath, pLimit, fCallback) =>
						{
							let tmpQuery = '/1.0/docs/history?limit=' + encodeURIComponent(pLimit)
								+ (pPath ? ('&path=' + encodeURIComponent(pPath)) : '');
							libFrameworkClient.getJSON(this.pict, tmpQuery)
								.then((pResult) => fCallback(null, pResult))
								.catch((pErr) => fCallback(pErr));
						},
						onSelectVersion: (pEvent, pPrevious, pPath) =>
						{
							let tmpDiff = this.pict.views['Plansheet-DocHistory-Diff'];
							if (tmpDiff) { tmpDiff.showDiff(pPrevious ? (pPrevious.Commit || '') : '', pEvent.Commit || '', pPath || ''); }
						}
					}),
				libDocHistory.TimelineView);
		}
		if (!this.pict.views['Plansheet-DocHistory-Diff'])
		{
			this.pict.addView('Plansheet-DocHistory-Diff',
				Object.assign({}, libDocHistory.DiffView.default_configuration,
					{
						onLoadDiff: (pFrom, pTo, pPath, fCallback) =>
						{
							let tmpQuery = '/1.0/docs/diff?to=' + encodeURIComponent(pTo)
								+ (pFrom ? ('&from=' + encodeURIComponent(pFrom)) : '')
								+ (pPath ? ('&path=' + encodeURIComponent(pPath)) : '');
							libFrameworkClient.getJSON(this.pict, tmpQuery)
								.then((pResult) => fCallback(null, pResult))
								.catch((pErr) => fCallback(pErr));
						}
					}),
				libDocHistory.DiffView);
		}
		this._historyViewsRegistered = true;
	}

	// The path of the document currently open in the reader (the inline-doc section tracks it).
	_currentDocPath()
	{
		let tmpState = this.pict.AppData.InlineDocumentation;
		return String((tmpState && tmpState.CurrentPath) || this._loadSlug || '').replace(/^\/+/, '').split('#')[0];
	}

	// Inline handler: open the history of the document being read.
	openHistory()
	{
		let tmpPath = this._currentDocPath();
		this._ensureHistoryViews();
		let tmpPanel = document.getElementById('FoundationDocs-HistoryPanel');
		if (tmpPanel) { tmpPanel.classList.add('ps-fdocs-history-open'); }
		this.pict.ContentAssignment.assignContent('#FoundationDocs-HistoryPath', tmpPath || 'the whole library');

		let tmpDiff = this.pict.views['Plansheet-DocHistory-Diff'];
		if (tmpDiff) { tmpDiff.clear(); }
		let tmpTimeline = this.pict.views['Plansheet-DocHistory-Timeline'];
		if (!tmpTimeline) { return; }
		// setPath reloads; the timeline selects the newest version, and selecting it drives the
		// diff pane, so opening history lands on "what changed most recently" with no extra click.
		tmpTimeline.setPath(tmpPath).then(() =>
		{
			let tmpSelected = tmpTimeline.selectedEvent();
			if (tmpSelected)
			{
				let tmpEvents = tmpTimeline.events();
				let tmpPrevious = tmpEvents[1] || (tmpSelected.Parent ? { Commit: tmpSelected.Parent } : null);
				if (tmpDiff) { tmpDiff.showDiff(tmpPrevious ? (tmpPrevious.Commit || '') : '', tmpSelected.Commit || '', tmpPath); }
			}
		});
	}

	// Inline handler: close the history panel.
	closeHistory()
	{
		let tmpPanel = document.getElementById('FoundationDocs-HistoryPanel');
		if (tmpPanel) { tmpPanel.classList.remove('ps-fdocs-history-open'); }
	}

	// ─── conflicts (WI #221) ─────────────────────────────────────────────
	// A conflict happens when the same document changed here and in the connected repository
	// at once. The reconciler keeps BOTH versions (so nothing is lost) by writing standard
	// markers into the text -- which is a developer's notation, and the person most likely to
	// be looking at it is an author who has never seen one. So the reader states the situation
	// in plain words and offers the decision as three buttons; the server picks the hunks.
	_refreshConflicts()
	{
		return libFrameworkClient.getJSON(this.pict, '/1.0/docs/tree')
			.then((pResult) =>
			{
				let tmpNodes = (pResult && pResult.Nodes) || [];
				this._conflictPaths = tmpNodes
					.filter((pNode) => String(pNode.ConflictState || '') === 'NeedsResolution')
					.map((pNode) => String(pNode.Path));
				this._renderConflictState();
			})
			// Silence, not an error surface: a failed check must not block reading or writing.
			.catch(() => { this._conflictPaths = []; this._renderConflictState(); });
	}

	_renderConflictState()
	{
		let tmpPaths = this._conflictPaths || [];
		let tmpCurrent = this._currentDocPath();
		let tmpIsCurrent = tmpPaths.indexOf(tmpCurrent) >= 0;

		// The banner: only for the document actually open, so it reads as being about what the
		// person is looking at rather than as a site-wide alarm.
		let tmpBanner = '';
		if (tmpIsCurrent)
		{
			tmpBanner = '<div class="ps-fdocs-conflict">'
				+ '<div class="ps-fdocs-conflict-text">'
				+ '<strong>This document was changed in two places at once.</strong> '
				+ 'It was edited here and in the connected repository before the two were brought together. '
				+ 'Both versions are kept below. Choose which one to keep.'
				+ '</div>'
				+ '<div class="ps-fdocs-conflict-actions">'
				+ '<button type="button" class="ps-fdocs-cbtn ps-fdocs-cbtn-primary" onclick="_Pict.views[\'Plansheet-FoundationDocs\'].resolveConflict(\'mine\')">Keep what I wrote</button>'
				+ '<button type="button" class="ps-fdocs-cbtn" onclick="_Pict.views[\'Plansheet-FoundationDocs\'].resolveConflict(\'theirs\')">Use the incoming version</button>'
				+ '<button type="button" class="ps-fdocs-cbtn" onclick="_Pict.views[\'Plansheet-FoundationDocs\'].editConflictByHand()">Let me edit it</button>'
				+ '</div></div>';
		}
		this.pict.ContentAssignment.assignContent('#FoundationDocs-ConflictBanner', tmpBanner);

		// The toolbar chip: the way to FIND a conflicted document that is not the one open.
		let tmpOthers = tmpPaths.filter((pPath) => pPath !== tmpCurrent);
		let tmpChip = '';
		if (tmpOthers.length)
		{
			let tmpLabel = (tmpOthers.length === 1) ? '1 document needs a decision' : (tmpOthers.length + ' documents need a decision');
			tmpChip = '<button type="button" class="ps-fdocs-cchip" title="Open the next one" onclick="_Pict.views[\'Plansheet-FoundationDocs\'].openNextConflict()">' + tmpLabel + '</button>';
		}
		this.pict.ContentAssignment.assignContent('#FoundationDocs-ConflictChip', tmpChip);
	}

	// The inline-doc provider memoizes fetched documents by URL and exposes no way to
	// invalidate one. A resolve changes a document's bytes SERVER-side without going through
	// the editor, so without this the reader would keep showing the pre-resolution text (with
	// its markers) until a full page reload. Dropping the whole cache is safe: it is only a
	// cache, and the next load refetches.
	_invalidateDocCache()
	{
		let tmpProvider = this._provider();
		if (tmpProvider && tmpProvider._ContentCache) { tmpProvider._ContentCache = {}; }
	}

	// Inline handler: jump to the next document waiting on a decision.
	openNextConflict()
	{
		let tmpCurrent = this._currentDocPath();
		let tmpNext = (this._conflictPaths || []).filter((pPath) => pPath !== tmpCurrent)[0];
		if (!tmpNext) { return; }
		let tmpProvider = this._provider();
		if (tmpProvider && typeof tmpProvider.loadDocument === 'function')
		{
			tmpProvider.loadDocument(tmpNext, () => this._renderConflictState());
		}
	}

	// Inline handler: take one side. The server derives both versions from the markers, so
	// nothing here has to parse or even mention them.
	resolveConflict(pResolution)
	{
		let tmpPath = this._currentDocPath();
		if (!tmpPath) { return; }
		libFrameworkClient.postJSON(this.pict, '/1.0/docs/resolve', { Path: tmpPath, Resolution: String(pResolution || '') })
			.then((pResult) =>
			{
				if (pResult && pResult.AlreadyResolved) { this._toast('That document was already resolved.', 'info'); }
				else { this._toast((pResolution === 'mine') ? 'Kept the version written here.' : 'Took the incoming version.', 'info'); }
				this._invalidateDocCache();
				let tmpProvider = this._provider();
				if (tmpProvider && typeof tmpProvider.loadDocument === 'function') { tmpProvider.loadDocument(tmpPath); }
				return this._refreshConflicts().then(() => this._renderPublishState());
			})
			.catch((pErr) => this._toast('Could not resolve this document: ' + ((pErr && pErr.message) || pErr), 'error'));
	}

	// Inline handler: the hand-edit path, kept for people who already read this notation. The
	// document still holds the merged text with its markers; the ordinary editor saves it, and
	// the save route clears the badge once the markers are gone.
	editConflictByHand()
	{
		this._toast('Open the editor and remove the parts you do not want, then save. The notice clears once the markers are gone.', 'info');
	}

	// ─── publishing indicator (WI #366) ──────────────────────────────────
	// Whether what an author wrote has reached the connected repository yet, in words
	// someone who has never run git can act on. A plan sheet with no repository (or a
	// read-only one) shows NOTHING here -- authoring without sync is the ordinary case,
	// so it gets no banner, no badge and no invitation to go connect something.
	_renderPublishState()
	{
		return libFrameworkClient.getJSON(this.pict, '/1.0/docs/publish-state')
			.then((pState) =>
			{
				let tmpHTML = '';
				if (pState && pState.Publishing)
				{
					let tmpRepo = String(pState.Repo || '').replace(/[<>&]/g, '');
					let tmpState = String(pState.State || '');
					let tmpLabel = 'Published' + (tmpRepo ? (' to ' + tmpRepo) : '');
					if (tmpState === 'pending') { tmpLabel = 'Not published yet'; }
					else if (tmpState === 'error') { tmpLabel = 'Last publish did not go through'; }
					tmpHTML = '<span class="ps-fdocs-publish ps-fdocs-publish-' + (tmpState || 'published') + '">'
						+ '<span class="ps-fdocs-publish-dot"></span>' + tmpLabel + '</span>';
				}
				this.pict.ContentAssignment.assignContent('#FoundationDocs-PublishState', tmpHTML);
				return pState;
			})
			// Silence, not an error surface: an author who cannot see the indicator can still write.
			.catch(() => { this.pict.ContentAssignment.assignContent('#FoundationDocs-PublishState', ''); });
	}

	// Inline edits (when EditEnabled) commit to the lake as a platform-source commit.
	_onSave(pSaveData, fCallback)
	{
		// The Overview and the sidebar are generated from the tree server-side, not
		// stored documents, so the save API rejects their underscore-prefixed paths.
		// Catch that here: a clear message beats a silent HTTP 400. Real docs fall through.
		let tmpPath = String((pSaveData && (pSaveData.Path || pSaveData.path)) || '').replace(/^\/+/, '');
		if (!tmpPath || tmpPath.charAt(0) === '_')
		{
			this._toast('The Overview is generated automatically. Open a specific document from the list to edit it.', 'error');
			return fCallback('The Overview page is not directly editable.');
		}
		libFrameworkClient.putJSON(this.pict, '/1.0/docs/save', pSaveData)
			.then(() =>
			{
				// The save just advanced the lake head, so the indicator flips to "not
				// published yet" until the next sync carries it out. A save that removed the
				// markers by hand also clears the badge server-side, so re-read that too.
				this._renderPublishState();
				this._refreshConflicts();
				fCallback(null);
			})
			.catch((pErr) => fCallback((pErr && pErr.message) || String(pErr)));
	}

	// ─── structure management callbacks (delegate to the Docs Lake API) ──────
	// Each mirrors the onSave contract: fCallback(errorOrNull). The inline-doc module
	// handles the confirm/prompt UI and reloads its sidebar on success; here we only
	// translate the callback into a DocLakeAPI call and normalise the error message.
	_docLake() { return this.pict.providers.DocLakeAPI; }

	// Join a parent folder and a leaf name into a clean lake path (the leaf may itself
	// carry slashes, e.g. "guides/setup", which nests under the parent).
	_joinPath(pParent, pLeaf)
	{
		let tmpParent = String(pParent || '').replace(/^\/+|\/+$/g, '');
		let tmpLeaf = String(pLeaf || '').replace(/^\/+|\/+$/g, '');
		return tmpParent ? (tmpParent + '/' + tmpLeaf) : tmpLeaf;
	}

	_settle(pPromise, fCallback)
	{
		pPromise
			.then(() => fCallback(null))
			.catch((pErr) => fCallback((pErr && pErr.message) || String(pErr)));
	}

	_onCreateFolder(pParentPath, pName, fCallback)
	{
		let tmpPath = this._joinPath(pParentPath, pName);
		if (!tmpPath) { return fCallback('A folder name is required.'); }
		this._settle(this._docLake().createFolder(tmpPath), fCallback);
	}

	_onCreateDocument(pParentPath, pName, fCallback)
	{
		let tmpPath = this._joinPath(pParentPath, pName);
		if (!tmpPath) { return fCallback('A document name is required.'); }
		if (!/\.md$/i.test(tmpPath)) { tmpPath = tmpPath + '.md'; }
		// A new document needs a first byte to exist; seed it with an H1 from its name.
		let tmpTitle = tmpPath.slice(tmpPath.lastIndexOf('/') + 1).replace(/\.md$/i, '').replace(/[-_]+/g, ' ').trim();
		let tmpBody = '# ' + (tmpTitle || 'New Document') + '\n';
		this._settle(this._docLake().saveDoc(tmpPath, tmpBody), fCallback);
	}

	_onDeleteNode(pPath, pNodeType, fCallback)
	{
		if (!pPath) { return fCallback('Nothing to delete.'); }
		this._settle(this._docLake().deleteNode(pPath), fCallback);
	}

	_onMoveNode(pFrom, pTo, fCallback)
	{
		if (!pFrom || !pTo) { return fCallback('A source and destination are required.'); }
		this._settle(this._docLake().moveNode(pFrom, pTo), fCallback);
	}

	_onReorder(pParentPath, pOrderedNames, fCallback)
	{
		this._settle(this._docLake().reorder(pParentPath || '', pOrderedNames || []), fCallback);
	}

	// The configured landing document for this plan sheet (DocsLandingPath on the session, set by a
	// doc sync to the repo overview; see fEnsureLandingPage). Falls back to index.md, which the server
	// serves as the generated Welcome placeholder when no index.md exists.
	_landingSlug()
	{
		let tmpUser = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {};
		let tmpLanding = (typeof tmpUser.DocsLandingPath === 'string') ? tmpUser.DocsLandingPath.trim() : '';
		return tmpLanding || 'index.md';
	}

	showOverview() { this._loadSlug = this._landingSlug(); this.render(); }

	showSlug(pPath)
	{
		let tmpPath = String(pPath || 'index');
		let tmpAnchor = '';
		let tmpHashIdx = tmpPath.indexOf('#');
		if (tmpHashIdx >= 0) { tmpAnchor = tmpPath.slice(tmpHashIdx); tmpPath = tmpPath.slice(0, tmpHashIdx); }
		// Append .md only for a bare slug; a path that already names a file type (an image, ...)
		// is left as-is so the reader can open it (loadDocument handles the extension too).
		this._loadSlug = (/\.[a-z0-9]+$/i.test(tmpPath) ? tmpPath : (tmpPath + '.md')) + tmpAnchor;
		this.render();
	}

	// The inline-doc section ships its nav outline collapsed and force-collapses
	// it again after every loadDocument (compact mode). We keep it open: default
	// the outline open, and after a navigation restore the open state if it was
	// open before the click (so a user who collapses it on purpose keeps it that
	// way). It also re-renders the nav on every load, which rebuilds the scroll
	// container (#InlineDoc-Nav-Outline) and snaps it back to the top - so we
	// snapshot and restore its scroll position too. Patches the instance once;
	// the section exposes no option for either behavior.
	_keepNavOpen(pProvider)
	{
		if (!pProvider || pProvider._navKeepOpenPatched || typeof pProvider.loadDocument !== 'function') { return; }
		let tmpView = this;
		let tmpOriginal = pProvider.loadDocument.bind(pProvider);
		let fScrollTop = () => { let tmpEl = document.getElementById('InlineDoc-Nav-Outline'); return tmpEl ? tmpEl.scrollTop : 0; };
		let fRestoreScroll = (pTop) =>
		{
			let tmpEl = document.getElementById('InlineDoc-Nav-Outline');
			if (tmpEl) { tmpEl.scrollTop = pTop; }
			// Re-apply after paint in case the re-render settles layout late.
			if (typeof requestAnimationFrame === 'function')
			{
				requestAnimationFrame(() => { let tmpLate = document.getElementById('InlineDoc-Nav-Outline'); if (tmpLate) { tmpLate.scrollTop = pTop; } });
			}
		};
		pProvider.loadDocument = function (pPath, fCallback)
		{
			let tmpState = tmpView.pict.AppData.InlineDocumentation;
			let tmpWasOpen = !!(tmpState && tmpState.NavCollapsed === false);
			let tmpScroll = fScrollTop();
			let tmpLoadAnchor = '';
			let tmpAnchorIdx = String(pPath).indexOf('#');
			if (tmpAnchorIdx >= 0) { tmpLoadAnchor = String(pPath).slice(tmpAnchorIdx + 1); }
			return tmpOriginal(pPath, function (pError, pHTML)
			{
				if (tmpWasOpen && tmpState && tmpState.NavCollapsed !== false)
				{
					tmpState.NavCollapsed = false;
					let tmpNav = tmpView.pict.views['InlineDoc-Nav'];
					if (tmpNav) { tmpNav.render(); }
				}
				fRestoreScroll(tmpScroll);
				// Keep the browser URL in sync with the loaded document (and the
				// section it targeted, if any) so links are copy/paste stable;
				// syncDocURL navigates idempotently and does not reload the doc.
				let tmpApp = tmpView.pict.PictApplication;
				if (tmpApp && typeof tmpApp.syncDocURL === 'function' && tmpState && tmpState.CurrentPath)
				{
					tmpApp.syncDocURL(tmpState.CurrentPath, tmpLoadAnchor);
				}
				if (typeof fCallback === 'function') { fCallback(pError, pHTML); }
			});
		};

		// Heading clicks (and anchored loads) call _scrollToAnchor; mirror the
		// scrolled-to section into the URL so section links are copy/paste stable.
		if (typeof pProvider._scrollToAnchor === 'function' && !pProvider._scrollSyncPatched)
		{
			let tmpScrollOriginal = pProvider._scrollToAnchor.bind(pProvider);
			pProvider._scrollToAnchor = function (pAnchor)
			{
				let tmpResult = tmpScrollOriginal(pAnchor);
				let tmpApp = tmpView.pict.PictApplication;
				let tmpScrollState = tmpView.pict.AppData.InlineDocumentation;
				if (tmpApp && !tmpApp._suppressDocURLSync && typeof tmpApp.syncDocURL === 'function' && tmpScrollState && tmpScrollState.CurrentPath && pAnchor)
				{
					tmpApp.syncDocURL(tmpScrollState.CurrentPath, pAnchor);
				}
				return tmpResult;
			};
			pProvider._scrollSyncPatched = true;
		}
		pProvider._navKeepOpenPatched = true;
	}

	// Set up the inline-doc provider ONCE: DocsBaseURL, edit callbacks, vocabulary, the sidebar tree. This
	// is what paints the tree into the sidebar's Documentation tab (the app re-points the Nav view there).
	// It deliberately does NOT load a document -- the reader loads the current doc separately in
	// _initSection -- so the Documentation tab can show the tree WITHOUT the reader being mounted and
	// WITHOUT changing the page. Returns a cached promise so repeat callers (the tab + the reader) share it.
	ensureDocsProviderInited()
	{
		if (this._docsInitPromise) { return this._docsInitPromise; }
		let tmpProvider = this._provider();
		if (!tmpProvider || typeof tmpProvider.initializeDocumentation !== 'function') { return Promise.resolve(); }
		this._keepNavOpen(tmpProvider);
		this._wireVocabRenderHook();
		// Editing the team library is a content.edit act: contributors get the edit affordances, everyone
		// else reads.
		let tmpApp = this.pict.PictApplication;
		let tmpCanEdit = !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can('content.edit'));
		let tmpInitOptions =
		{
			DocsBaseURL: '/1.0/docs/',
			ContainerAddress: '#InlineDoc-Container',
			EditEnabled: tmpCanEdit,
			// Topic Management is a dead feature (no persistence); the F1 Help Drawer is the real one. WI #185.
			TopicManagerEnabled: false,
			AssetToggleEnabled: true,
			// The tree lists files (and folders) only; the in-document heading outline is noise here.
			ShowDocumentHeadings: false,
			onSave: (pData, fCb) => this._onSave(pData, fCb),
			VocabularyResolver: (pWord) => this._vocabularyResolver(pWord)
		};
		if (tmpCanEdit)
		{
			tmpInitOptions.onCreateFolder = (pParentPath, pName, fCb) => this._onCreateFolder(pParentPath, pName, fCb);
			tmpInitOptions.onCreateDocument = (pParentPath, pName, fCb) => this._onCreateDocument(pParentPath, pName, fCb);
			tmpInitOptions.onDeleteNode = (pPath, pNodeType, fCb) => this._onDeleteNode(pPath, pNodeType, fCb);
			tmpInitOptions.onMoveNode = (pFrom, pTo, fCb) => this._onMoveNode(pFrom, pTo, fCb);
			tmpInitOptions.onReorder = (pParentPath, pOrderedNames, fCb) => this._onReorder(pParentPath, pOrderedNames, fCb);
		}
		let tmpSelf = this;
		// Wait for the vocabulary map before init so the first document paints with highlights.
		this._docsInitPromise = this._loadVocabularyMap().then(() => new Promise((pResolve) =>
		{
			tmpProvider.initializeDocumentation(tmpInitOptions, () =>
			{
				// Default the navigation outline open (the section ships it collapsed).
				if (tmpSelf.pict.AppData.InlineDocumentation) { tmpSelf.pict.AppData.InlineDocumentation.NavCollapsed = false; }
				pResolve();
			});
		}));
		return this._docsInitPromise;
	}

	// On a reader render: make sure the provider is set up, then render the article layout into the reader
	// (creating the content pane) and load the requested document. The tree itself lives in the sidebar.
	_initSection()
	{
		let tmpProvider = this._provider();
		if (!tmpProvider || typeof tmpProvider.initializeDocumentation !== 'function')
		{
			this.pict.ContentAssignment.assignContent('#InlineDoc-Container',
				'<div style="padding:24px;color:var(--theme-color-text-secondary,#5b6376)">Documentation section is not available in this build.</div>');
			return;
		}
		let tmpSelf = this;
		this.ensureDocsProviderInited().then(() =>
		{
			// The reader is mounted now, so (re)render the inline-doc layout into #InlineDoc-Container to
			// create the article pane, then load the requested doc into it.
			let tmpLayout = tmpSelf.pict.views['InlineDoc-Layout'];
			if (tmpLayout && typeof tmpLayout.render === 'function') { tmpLayout.render(); }
			tmpProvider.loadDocument(tmpSelf._loadSlug || 'index.md');
		});
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMessage, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 2500 });
		}
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		this._initSection();
		this._renderPublishState();
		this._refreshConflicts();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewFoundationDocs;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-FoundationDocs',
	DefaultRenderable: 'FoundationDocs-Display',
	AutoRender: false,
	CSS: /*css*/`
		.ps-fdocs { height: 100%; display: flex; flex-direction: column; box-sizing: border-box; position: relative; }
		.ps-fdocs-toolbar {
			display: flex; align-items: center; gap: 8px; padding: 12px 20px;
			border-bottom: 1px solid var(--theme-color-border-default, #e6e9ee);
			background: var(--theme-color-background-panel, #fff);
		}
		.ps-fdocs-title { font-size: 16px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); }
		.ps-fdocs-spacer { flex: 1; }

		/* Publishing indicator (WI #366): a quiet status line, not a dashboard. Absent
		   entirely when the plan sheet publishes nowhere. */
		.ps-fdocs-publish {
			display: inline-flex; align-items: center; gap: 6px;
			font-size: 12px; color: var(--theme-color-text-secondary, #5b6376);
			white-space: nowrap;
		}
		.ps-fdocs-publish-dot {
			width: 7px; height: 7px; border-radius: 50%;
			background: var(--theme-color-text-secondary, #5b6376);
			flex: none;
		}
		.ps-fdocs-publish-published .ps-fdocs-publish-dot { background: var(--theme-color-status-success, #2e9e5b); }
		.ps-fdocs-publish-pending .ps-fdocs-publish-dot { background: var(--theme-color-status-warning, #d0902f); }
		.ps-fdocs-publish-error .ps-fdocs-publish-dot { background: var(--theme-color-status-error, #c8452f); }
		.ps-fdocs-publish-error { color: var(--theme-color-status-error, #c8452f); }

		/* The conflict notice. Stated in words, above the document it is about; a person never
		   has to know what a marker is to act on it. */
		.ps-fdocs-conflict {
			display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
			padding: 12px 20px;
			border-bottom: 1px solid var(--theme-color-border-default, #e6e9ee);
			border-left: 3px solid var(--theme-color-status-warning, #d0902f);
			background: color-mix(in srgb, var(--theme-color-status-warning, #d0902f) 12%, transparent);
			color: var(--theme-color-text-primary, #2c3140);
			font-size: 13px; line-height: 1.5;
		}
		.ps-fdocs-conflict-text { flex: 1; min-width: 260px; }
		.ps-fdocs-conflict-actions { display: flex; gap: 8px; flex-wrap: wrap; }
		.ps-fdocs-cbtn {
			border: 1px solid var(--theme-color-border-strong, #46505c);
			background: var(--theme-color-background-panel, #fff);
			color: var(--theme-color-text-primary, #2c3140);
			border-radius: 6px; padding: 6px 12px; font-size: 12px; cursor: pointer; white-space: nowrap;
		}
		.ps-fdocs-cbtn:hover { background: var(--theme-color-background-hover, #f4f6fa); }
		.ps-fdocs-cbtn-primary {
			border-color: var(--theme-color-brand-primary, #4f95e8);
			color: var(--theme-color-brand-primary, #4f95e8);
			font-weight: 600;
		}
		.ps-fdocs-cchip {
			border: 1px solid var(--theme-color-status-warning, #d0902f);
			background: transparent; color: var(--theme-color-status-warning, #d0902f);
			border-radius: 999px; padding: 3px 10px; font-size: 12px; cursor: pointer; white-space: nowrap;
		}
		.ps-fdocs-cchip:hover { background: color-mix(in srgb, var(--theme-color-status-warning, #d0902f) 15%, transparent); }

		.ps-fdocs-histbtn {
			border: 1px solid var(--theme-color-border-default, #e6e9ee);
			background: var(--theme-color-background-panel, #fff);
			color: var(--theme-color-text-secondary, #5b6376);
			border-radius: 6px; padding: 4px 10px; font-size: 12px; cursor: pointer;
		}
		.ps-fdocs-histbtn:hover {
			background: var(--theme-color-background-hover, #f4f6fa);
			color: var(--theme-color-text-primary, #2c3140);
		}

		/* The history panel covers the reader rather than squeezing it: the reader's own
		   layout is a three-pane thing already, and shrinking it to fit a diff makes both
		   unreadable. Hidden (not just transparent) when closed so it takes no hit testing. */
		.ps-fdocs-history {
			display: none; position: absolute; inset: 0; z-index: 40;
			background: var(--theme-color-background-panel, #fff);
			flex-direction: column;
		}
		.ps-fdocs-history-open { display: flex; }
		.ps-fdocs-history-bar {
			display: flex; align-items: center; gap: 8px; padding: 10px 16px;
			border-bottom: 1px solid var(--theme-color-border-default, #e6e9ee);
			font-size: 13px; color: var(--theme-color-text-primary, #2c3140);
		}
		.ps-fdocs-history-title { font-weight: 600; }
		.ps-fdocs-history-path { font-weight: 400; color: var(--theme-color-text-secondary, #5b6376); overflow-wrap: anywhere; }
		.ps-fdocs-history-body { flex: 1; min-height: 0; display: flex; }
		.ps-fdocs-history-left {
			width: 320px; flex: none; min-height: 0; overflow: hidden; display: flex;
			border-right: 1px solid var(--theme-color-border-default, #e6e9ee);
		}
		.ps-fdocs-history-left > * { flex: 1; min-height: 0; }
		.ps-fdocs-history-right { flex: 1; min-width: 0; min-height: 0; overflow: hidden; }
		/* Narrow: stack the timeline over the diff instead of a 320px column crushing it. */
		@media (max-width: 900px) {
			.ps-fdocs-history-body { flex-direction: column; }
			.ps-fdocs-history-left { width: auto; max-height: 40%; border-right: 0; border-bottom: 1px solid var(--theme-color-border-default, #e6e9ee); }
		}
		.ps-fdocs-host { flex: 1; min-height: 0; overflow: hidden; }
		.ps-fdocs-host #InlineDoc-Container, #InlineDoc-Container { height: 100%; }

		/* Vocabulary auto-markup (WI #184): known product terms get a dotted underline; hover/focus
		   pops the definition (pict-modal-tooltip's richTooltip, wired in _wireVocabPopovers). */
		.pict-vocab-term {
			border-bottom: 1px dotted var(--theme-color-brand-primary, #4f95e8);
			cursor: help;
		}
		.pict-vocab-popover-title {
			font-weight: 600;
			color: var(--theme-color-text-primary, #2c3140);
			margin-bottom: 4px;
		}
		.pict-vocab-popover-short {
			color: var(--theme-color-text-secondary, #5b6376);
			font-size: 13px;
			line-height: 1.4;
		}
		.pict-vocab-popover-link {
			display: inline-block;
			margin-top: 6px;
			font-size: 12px;
			color: var(--theme-color-brand-primary, #4f95e8);
			text-decoration: none;
		}
		.pict-vocab-popover-link:hover { text-decoration: underline; }
	`,
	Templates:
	[
		{
			Hash: 'FoundationDocs-Display',
			Template: /*html*/`
<div class="ps-fdocs">
	<div class="ps-fdocs-toolbar">
		<span class="ps-fdocs-spacer"></span>
		<span id="FoundationDocs-ConflictChip"></span>
		<span id="FoundationDocs-PublishState"></span>
		<button type="button" class="ps-fdocs-histbtn" title="See how this document changed" onclick="_Pict.views['Plansheet-FoundationDocs'].openHistory()">History</button>
	</div>
	<div id="FoundationDocs-ConflictBanner"></div>
	<div id="InlineDoc-Container" class="ps-fdocs-host"></div>
	<div id="FoundationDocs-HistoryPanel" class="ps-fdocs-history">
		<div class="ps-fdocs-history-bar">
			<span class="ps-fdocs-history-title">History of <span id="FoundationDocs-HistoryPath" class="ps-fdocs-history-path"></span></span>
			<span class="ps-fdocs-spacer"></span>
			<button type="button" class="ps-fdocs-histbtn" onclick="_Pict.views['Plansheet-FoundationDocs'].closeHistory()">Close</button>
		</div>
		<div class="ps-fdocs-history-body">
			<div id="DocHistory-Timeline-Container" class="ps-fdocs-history-left"></div>
			<div id="DocHistory-Diff-Container" class="ps-fdocs-history-right"></div>
		</div>
	</div>
</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'FoundationDocs-Display',
			TemplateHash: 'FoundationDocs-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
